import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useDarkMode } from '../ui/darkMode';
import { updateMinhasPreferencias } from '../services/userService';
import { UI_THEME } from '../ui/theme';
import { FIELD_CLASS } from '../components/ui/fieldStyles';
import { buttonClassName } from '../components/ui/buttonStyles';
import { Permission } from '../types/user.types';
import MeuPerfilPublicoForm from '../components/MeuPerfilPublicoForm';
import { toUpperText } from '../utils/formatters';

type ProfileTab = 'perfil' | 'preferencias' | 'publico';

const ProfilePage: React.FC = () => {
  const { user, refreshUser, hasPermission } = useAuth();
  const [activeTab, setActiveTab] = useState<ProfileTab>('perfil');
  const [isDark, toggleDark] = useDarkMode();
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [savingEmailPreference, setSavingEmailPreference] = useState(false);
  const [preferencesMessage, setPreferencesMessage] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    username: '',
    email: '',
    fullName: '',
  });

  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [infoMessage, setInfoMessage] = useState<string | null>(null);

  const isSaas = UI_THEME === 'saas';
  const surfaceClass = 'border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900/80';
  const heroClass = isSaas
    ? 'border-teal-100 bg-gradient-to-br from-white via-teal-50/70 to-indigo-50 p-6 shadow-sm dark:border-slate-700 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800'
    : 'border-slate-200 bg-gradient-to-br from-white via-slate-50 to-blue-50 p-6 shadow-sm dark:border-slate-700 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800';
  const accentTextClass = isSaas ? 'text-teal-700 dark:text-teal-300' : 'text-blue-700 dark:text-blue-300';
  const bodyTextClass = 'text-slate-600 dark:text-slate-300';
  const labelTextClass = 'text-slate-500 dark:text-slate-400';
  const strongTextClass = 'text-slate-900 dark:text-slate-100';
  const inputClass = `${FIELD_CLASS} mt-1`;
  const primaryButtonClass = buttonClassName({ variant: 'primary' });
  const secondaryButtonClass = buttonClassName({ variant: 'secondary' });

  useEffect(() => {
    if (user) {
      setFormData((prev) => ({
        ...prev,
        username: user.username || '',
        email: user.email || '',
        fullName: user.fullName || user.name || '',
      }));
      if (typeof user.receberEmailsNotificacao === 'boolean') {
        setEmailNotifications(user.receberEmailsNotificacao);
      }
    }
  }, [user]);

  useEffect(() => {
    if (!infoMessage) return;
    const timer = setTimeout(() => setInfoMessage(null), 4000);
    return () => clearTimeout(timer);
  }, [infoMessage]);

  useEffect(() => {
    if (!preferencesMessage) return;
    const timer = setTimeout(() => setPreferencesMessage(null), 4000);
    return () => clearTimeout(timer);
  }, [preferencesMessage]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: name === 'fullName' ? toUpperText(value) : value }));
  };

  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setInfoMessage('Funcionalidade de atualização de perfil será implementada.');
    setIsEditingProfile(false);
  };

  const handleToggleEmailNotifications = async () => {
    const next = !emailNotifications;
    setEmailNotifications(next);
    setSavingEmailPreference(true);
    try {
      await updateMinhasPreferencias(next);
      refreshUser();
      setPreferencesMessage(
        next
          ? 'Notificações por e-mail reativadas.'
          : 'Notificações por e-mail desativadas. E-mails de login, senha e aprovação de cadastro continuam sendo enviados normalmente.'
      );
    } catch {
      setEmailNotifications(!next);
      setPreferencesMessage('Não foi possível salvar a preferência. Tente novamente.');
    } finally {
      setSavingEmailPreference(false);
    }
  };

  const tabs: { key: ProfileTab; label: string }[] = [
    { key: 'perfil', label: 'Perfil' },
    ...(hasPermission(Permission.EDIT_PERFIL_PUBLICO_PARLAMENTAR)
      ? [{ key: 'publico' as ProfileTab, label: 'Página pública' }]
      : []),
    { key: 'preferencias', label: 'Preferências' },
  ];

  const navItemClass = (tab: ProfileTab) => {
    const active = activeTab === tab;
    if (active) {
      return isSaas
        ? 'bg-teal-50 text-teal-700 dark:bg-teal-500/10 dark:text-teal-300'
        : 'bg-blue-50 text-blue-700 dark:bg-blue-500/10 dark:text-blue-300';
    }
    return 'text-slate-600 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-800';
  };

  const profileSummary = [
    { label: 'Usuário', value: user?.username || 'Desconhecido', dot: isSaas ? 'bg-teal-500' : 'bg-blue-600' },
    { label: 'Perfil', value: user?.role || 'Usuário', dot: 'bg-emerald-500' },
    { label: 'E-mail', value: user?.email || 'N/A', dot: 'bg-slate-400' },
  ];

  return (
    <div className="w-full space-y-6">
      <section className={`rounded-3xl border ${heroClass}`}>
        <p className={`text-xs font-semibold uppercase tracking-[0.2em] ${accentTextClass}`}>
          {isSaas ? 'Conta' : 'Meu perfil'}
        </p>
        <h1 className={`mt-2 text-2xl font-bold tracking-tight sm:text-3xl ${strongTextClass}`}>
          Perfil do usuário
        </h1>
        <p className={`mt-2 text-sm leading-6 ${bodyTextClass}`}>
          Consulte suas informações de acesso e mantenha seus dados de segurança atualizados.
        </p>

        <div className={`mt-5 overflow-hidden rounded-2xl border ${surfaceClass}`}>
          <div className="grid divide-y divide-slate-200 dark:divide-slate-700 md:grid-cols-3 md:divide-x md:divide-y-0">
            {profileSummary.map((item) => (
              <div key={item.label} className="px-4 py-2">
                <p className={`flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.16em] ${labelTextClass}`}>
                  <span className={`h-1.5 w-1.5 rounded-full ${item.dot}`} />
                  {item.label}
                </p>
                <p className={`mt-1 truncate text-sm font-semibold ${strongTextClass}`}>{item.value}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {(activeTab === 'perfil' ? infoMessage : activeTab === 'preferencias' ? preferencesMessage : null) && (
        <div className={`rounded-2xl border px-4 py-3 text-sm font-medium ${
          isSaas
            ? 'border-teal-200 bg-teal-50 text-teal-800 dark:border-teal-500/30 dark:bg-teal-500/10 dark:text-teal-200'
            : 'border-blue-200 bg-blue-50 text-blue-800 dark:border-blue-500/30 dark:bg-blue-500/10 dark:text-blue-200'
        }`}>
          {activeTab === 'perfil' ? infoMessage : preferencesMessage}
        </div>
      )}

      <section className="grid gap-6 lg:grid-cols-[220px_minmax(0,1fr)]">
        <nav className={`h-fit rounded-3xl border p-2 ${surfaceClass}`}>
          {tabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveTab(tab.key)}
              className={`block w-full rounded-xl px-4 py-2.5 text-left text-sm font-semibold transition ${navItemClass(tab.key)}`}
            >
              {tab.label}
            </button>
          ))}
        </nav>

        {activeTab === 'publico' ? (
          <div className={`rounded-3xl border ${surfaceClass}`}>
            <div className="border-b border-slate-200 px-5 py-4 dark:border-slate-800">
              <h2 className={`text-lg font-semibold ${strongTextClass}`}>Página pública</h2>
              <p className={`mt-1 text-sm ${bodyTextClass}`}>Dados pessoais que você escolhe divulgar na sua página do Portal da Transparência.</p>
            </div>
            <MeuPerfilPublicoForm />
          </div>
        ) : activeTab === 'perfil' ? (
        <div className={`rounded-3xl border ${surfaceClass}`}>
          <div className="border-b border-slate-200 px-5 py-4 dark:border-slate-800">
            <h2 className={`text-lg font-semibold ${strongTextClass}`}>Informações pessoais</h2>
            <p className={`mt-1 text-sm ${bodyTextClass}`}>Dados básicos vinculados ao seu acesso.</p>
          </div>
          <form onSubmit={handleSaveProfile} className="space-y-4 p-5">
            <div>
              <label htmlFor="username" className={`block text-sm font-medium ${labelTextClass}`}>Nome de usuário</label>
              <input
                type="text"
                id="username"
                name="username"
                value={formData.username}
                onChange={handleChange}
                disabled={!isEditingProfile}
                className={inputClass}
              />
            </div>
            <div>
              <label htmlFor="email" className={`block text-sm font-medium ${labelTextClass}`}>E-mail</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                disabled={!isEditingProfile}
                className={inputClass}
              />
            </div>
            <div>
              <label htmlFor="fullName" className={`block text-sm font-medium ${labelTextClass}`}>Nome completo</label>
              <input
                type="text"
                id="fullName"
                name="fullName"
                value={formData.fullName}
                onChange={handleChange}
                disabled={!isEditingProfile}
                className={inputClass}
              />
            </div>

            <div className="flex flex-wrap gap-3 pt-2">
              {!isEditingProfile ? (
                <button
                  type="button"
                  onClick={() => setIsEditingProfile(true)}
                  className={`${primaryButtonClass}`}
                >
                  Editar perfil
                </button>
              ) : (
                <>
                  <button type="submit" className={`${primaryButtonClass}`}>
                    Salvar alterações
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsEditingProfile(false)}
                    className={`${secondaryButtonClass}`}
                  >
                    Cancelar
                  </button>
                </>
              )}
            </div>
          </form>
        </div>
        ) : (
          <div className={`rounded-3xl border ${surfaceClass}`}>
            <div className="border-b border-slate-200 px-5 py-4 dark:border-slate-800">
              <h2 className={`text-lg font-semibold ${strongTextClass}`}>Preferências</h2>
              <p className={`mt-1 text-sm ${bodyTextClass}`}>Personalize a aparência e as notificações da sua conta.</p>
            </div>
            <div className="space-y-6 p-5">
              <div className="flex items-center justify-between gap-4 rounded-2xl border border-slate-200 p-4 dark:border-slate-700">
                <div>
                  <p className={`text-sm font-semibold ${strongTextClass}`}>Tema escuro</p>
                  <p className={`mt-0.5 text-xs ${labelTextClass}`}>Alterna a aparência do sistema entre claro e escuro.</p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={isDark}
                  onClick={toggleDark}
                  className={`relative h-6 w-11 shrink-0 rounded-full transition ${isDark ? (isSaas ? 'bg-teal-600' : 'bg-blue-700') : 'bg-slate-300 dark:bg-slate-700'}`}
                >
                  <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-surface shadow transition ${isDark ? 'left-5' : 'left-0.5'}`} />
                </button>
              </div>

              <div className="flex items-center justify-between gap-4 rounded-2xl border border-slate-200 p-4 dark:border-slate-700">
                <div>
                  <p className={`text-sm font-semibold ${strongTextClass}`}>Notificações por e-mail</p>
                  <p className={`mt-0.5 text-xs ${labelTextClass}`}>
                    Receber avisos sobre movimentações relevantes (emendas, planos de trabalho, documentos etc.) por e-mail.
                  </p>
                  <p className={`mt-1 text-xs ${labelTextClass}`}>
                    E-mails de login, redefinição de senha e aprovação de cadastro são sempre enviados, independente desta opção.
                  </p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={emailNotifications}
                  onClick={handleToggleEmailNotifications}
                  disabled={savingEmailPreference}
                  className={`relative h-6 w-11 shrink-0 rounded-full transition disabled:opacity-60 ${emailNotifications ? (isSaas ? 'bg-teal-600' : 'bg-blue-700') : 'bg-slate-300 dark:bg-slate-700'}`}
                >
                  <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-surface shadow transition ${emailNotifications ? 'left-5' : 'left-0.5'}`} />
                </button>
              </div>
            </div>
          </div>
        )}
      </section>
    </div>
  );
};

export default ProfilePage;
